var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppState } from 'context/AppState';
import { useViewport } from 'hooks/useViewport';
import { Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import React, { useCallback } from 'react';
import { mergeSx } from 'utils/styles';
import { Icon } from '@petsly/shared-client';
import { useUser } from 'context/AuthContext';
import { NavEntry } from './NavEntry';
import styles from './Sidebar.styles';
import Routes from 'routes/Routes';
import { UserMenu } from './UserMenu';
import { Search } from './Search';
import { FormattedMessage } from 'react-intl';
import { LineStyleTwoTone, ListTwoTone, CalendarMonthTwoTone, PeopleTwoTone, BusinessCenterTwoTone, PlaceTwoTone, BadgeTwoTone, CopyAllTwoTone, CampaignTwoTone, } from '@mui/icons-material';
export var SIDEBAR_WIDTH = 7.6;
export var SIDEBAR_EXTENDED_WIDTH = 28;
export var SIDEBAR_EXTENDED_SPACING = 2.4;
export var SIDEBAR_SPACING = 1.7;
export var Sidebar = function () {
    var pathname = useLocation().pathname;
    var userTenant = useUser().userTenant;
    var _a = useViewport(), isSm = _a.isSm, isMd = _a.isMd, isXs = _a.isXs;
    var _b = useAppState(), isExpanded = _b.isExpanded, isFixed = _b.isFixed, isNativeApp = _b.isNativeApp, setIsExpanded = _b.setIsExpanded;
    var isOpened = isExpanded || isFixed;
    var content = [
        {
            label: 'app.sidebar.dashboard',
            icon: function () { return _jsx(LineStyleTwoTone, {}, void 0); },
            href: Routes.DASHBOARD,
            permission: true,
            dataCypress: 'nav-dashboard',
        },
        {
            label: 'nav.appointments',
            icon: function () { return _jsx(ListTwoTone, {}, void 0); },
            href: Routes.APPOINTMENTS,
            permission: true,
            dataCypress: 'nav-appointments',
        },
        {
            label: 'nav.calendar',
            icon: function () { return _jsx(CalendarMonthTwoTone, {}, void 0); },
            href: Routes.CALENDAR,
            permission: true,
            dataCypress: 'nav-calendar',
        },
        {
            label: 'nav.customers',
            icon: function () { return _jsx(PeopleTwoTone, {}, void 0); },
            href: Routes.CUSTOMERS,
            permission: true,
            dataCypress: 'nav-customers',
        },
    ];
    var salesContent = [
        {
            label: 'app.sidebar.sales.services',
            icon: function () { return _jsx(Icon, { size: 20, name: "dollar", color: "inherit" }, void 0); },
            permission: true,
            dataCypress: 'nav.sales.services',
            href: Routes.MERCHANT,
            childrens: [
                {
                    label: 'app.sidebar.sales.services.list',
                    href: Routes.MERCHANT_SERVICES,
                    permission: true,
                    dataCypress: 'sidebar.sales.services.list',
                },
                {
                    label: 'app.sidebar.sales.services.category-list',
                    href: Routes.MERCHANT_SERVICE_CATEGORIES,
                    permission: true,
                    dataCypress: 'sidebar.sales.ervices.category-list',
                },
            ],
        },
        {
            label: 'app.sidebar.sales.invoices',
            icon: function () { return _jsx(Icon, { size: 20, name: "dollar", color: "inherit" }, void 0); },
            permission: true,
            dataCypress: 'nav.sales.invoices',
            href: Routes.MERCHANT,
            childrens: [
                {
                    label: 'app.sidebar.sales.invoices.list',
                    href: Routes.INVOICES,
                    permission: true,
                    dataCypress: 'sidebar.sales.invoices.list',
                },
                {
                    label: 'app.sidebar.sales.tax-rates',
                    href: Routes.MERCHANT_SALES,
                    permission: true,
                    dataCypress: 'sidebar.sales.tax-rates',
                },
            ],
        },
    ];
    var companyContent = [
        {
            label: 'app.sidebar.company.company-details',
            icon: function () { return _jsx(BusinessCenterTwoTone, {}, void 0); },
            permission: (userTenant === null || userTenant === void 0 ? void 0 : userTenant.role) === 'ADMIN',
            href: Routes.MERCHANT_PROFILE,
            dataCypress: 'app.sidebar.company.company-details',
        },
        {
            label: 'app.sidebar.company.company-branches',
            icon: function () { return _jsx(PlaceTwoTone, {}, void 0); },
            permission: (userTenant === null || userTenant === void 0 ? void 0 : userTenant.role) === 'ADMIN',
            href: Routes.MERCHANT_LOCATIONS,
            dataCypress: 'app.sidebar.company.company-branches',
        },
        {
            label: 'app.sidebar.company.employees',
            icon: function () { return _jsx(BadgeTwoTone, {}, void 0); },
            permission: (userTenant === null || userTenant === void 0 ? void 0 : userTenant.role) === 'ADMIN',
            href: Routes.MERCHANT_EMPLOYEES,
            dataCypress: 'app.sidebar.company.employees',
        },
        {
            label: 'app.sidebar.company.templates',
            icon: function () { return _jsx(CopyAllTwoTone, {}, void 0); },
            permission: (userTenant === null || userTenant === void 0 ? void 0 : userTenant.role) === 'ADMIN',
            href: Routes.MERCHANT_TEMPLATES,
            dataCypress: 'app.sidebar.company.templates',
        },
        {
            label: 'app.sidebar.company.notifications',
            icon: function () { return _jsx(CampaignTwoTone, {}, void 0); },
            permission: (userTenant === null || userTenant === void 0 ? void 0 : userTenant.role) === 'ADMIN',
            href: Routes.MERCHANT_NOTIFICATIONS,
            dataCypress: 'app.sidebar.company.notifications',
        },
    ];
    var sidebarWidth = useCallback(function () {
        if (!isSm) {
            return isOpened ? '256px' : '76px';
        }
        return isOpened ? (isXs ? '100%' : '256px') : 0;
    }, [isXs, isSm, isOpened]);
    return (_jsxs(Stack, __assign({ displayPrint: "none", sx: mergeSx(styles.container, {
            width: function () { return sidebarWidth(); },
        }, isNativeApp && styles.nativeApp), spacing: 2.5 }, { children: [_jsx(UserMenu, {}, void 0), _jsx(Search, {}, void 0), _jsx(Stack, { children: content
                    .filter(function (entry) { return entry.permission; })
                    .map(function (_a, index) {
                    var label = _a.label, Icon = _a.icon, childrens = _a.childrens, dataCypress = _a.dataCypress, _b = _a.href, href = _b === void 0 ? Routes.HOME : _b;
                    return (_jsx(NavEntry, { icon: Icon, label: label, expanded: isOpened, fixed: isFixed, href: href, childrens: childrens, dataCypress: dataCypress, selected: pathname.startsWith(href), onClick: function () {
                            if (!isMd) {
                                return;
                            }
                            setIsExpanded(false);
                        } }, index));
                }) }, void 0), _jsxs(Stack, __assign({ spacing: "14px" }, { children: [_jsx(Typography, __assign({ variant: "text-sm", color: "#878D97", fontWeight: 500 }, { children: _jsx(FormattedMessage, { id: "app.sidebar.sales.heading" }, void 0) }), void 0), _jsx(Stack, { children: salesContent
                            .filter(function (entry) { return entry.permission; })
                            .map(function (_a, index) {
                            var label = _a.label, Icon = _a.icon, childrens = _a.childrens, dataCypress = _a.dataCypress, _b = _a.href, href = _b === void 0 ? Routes.HOME : _b;
                            return (_jsx(NavEntry, { icon: Icon, label: label, expanded: isOpened, fixed: isFixed, href: href, childrens: childrens, dataCypress: dataCypress, selected: pathname.startsWith(href), onClick: function () {
                                    if (!isMd) {
                                        return;
                                    }
                                    setIsExpanded(false);
                                } }, index));
                        }) }, void 0)] }), void 0), _jsxs(Stack, __assign({ spacing: "14px" }, { children: [_jsx(Typography, __assign({ variant: "text-sm", color: "#878D97", fontWeight: 500 }, { children: _jsx(FormattedMessage, { id: "app.sidebar.company.heading" }, void 0) }), void 0), _jsx(Stack, { children: companyContent
                            .filter(function (entry) { return entry.permission; })
                            .map(function (_a, index) {
                            var label = _a.label, Icon = _a.icon, childrens = _a.childrens, dataCypress = _a.dataCypress, _b = _a.href, href = _b === void 0 ? Routes.HOME : _b;
                            return (_jsx(NavEntry, { icon: Icon, label: label, expanded: isOpened, fixed: isFixed, href: href, childrens: childrens, dataCypress: dataCypress, selected: pathname.startsWith(href), onClick: function () {
                                    if (!isMd) {
                                        return;
                                    }
                                    setIsExpanded(false);
                                } }, index));
                        }) }, void 0)] }), void 0)] }), void 0));
};
